'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '@/lib/api';

interface NepaliDate {
  year: number;
  month: number;
  day: number;
  monthName?: string;
  dayName?: string;
  formatted?: string;
}

interface EnglishDate {
  year: number;
  month: number;
  day: number;
  formatted?: string;
}

interface DateData {
  nepali: NepaliDate;
  english: EnglishDate;
  timestamp: string;
}

interface DateContextType {
  dateData: DateData | null;
  loading: boolean;
  error: string | null;
  refreshDate: () => Promise<void>;
  setDateData: (d: DateData | null) => void;
}

const DateContext = createContext<DateContextType | undefined>(undefined);

// Session-only in-memory cache — not stored in cookies or localStorage
let cachedDate: DateData | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 60 * 1000; // 1 minute — refresh every minute
let pendingRequest: Promise<void> | null = null;

export function DateProvider({ children }: { children: ReactNode }) {
  const [dateData, setDateData] = useState<DateData | null>(cachedDate);
  const [loading, setLoading] = useState(!cachedDate);
  const [error, setError] = useState<string | null>(null);

  const refreshDate = async () => {
    // Check cache first
    const now = Date.now();
    if (cachedDate && (now - cacheTimestamp) < CACHE_TTL) {
      setDateData(cachedDate);
      setLoading(false);
      return;
    }

    // If there's already a pending request, wait for it
    if (pendingRequest) {
      await pendingRequest;
      if (cachedDate) {
        setDateData(cachedDate);
        setLoading(false);
      }
      return;
    }

    // Create new request
    pendingRequest = (async () => {
      try {
        setLoading(true);
        setError(null);
        const response: any = await apiService.getCurrentDate();
        
        if (response?.success && response?.data) {
          cachedDate = response.data;
          cacheTimestamp = now;
          setDateData(cachedDate);
        } else {
          throw new Error('Failed to get date data');
        }
      } catch (err: any) {
        console.error('Failed to fetch date:', err);
        // Don't set error state - just use fallback
        // This prevents error boundary from catching date fetch failures
        setError(null); // Clear error state to prevent UI issues
        
        // Use cached data if available, even if expired
        if (cachedDate) {
          setDateData(cachedDate);
          // Don't update cacheTimestamp — let it retry on next refresh
        }
        // If no cache at all, show nothing; next interval/retry will fill it in
      } finally {
        setLoading(false);
        pendingRequest = null;
      }
    })();

    await pendingRequest;
  };

  // Allow manual selection of date (e.g., when user clicks a calendar cell)
  const setDateDataAndCache = (d: DateData | null) => {
    setDateData(d);
    if (d) {
      cachedDate = d;
      cacheTimestamp = Date.now();
    }
  };

  useEffect(() => {
    // Use requestIdleCallback for non-critical date fetch to improve FCP
    const fetchDateDelayed = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          refreshDate();
        }, { timeout: 1000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          refreshDate();
        }, 100);
      }
    };
    
    fetchDateDelayed();

    // Refresh every minute
    const interval = setInterval(refreshDate, CACHE_TTL);

    // Schedule a forced refresh at midnight to update the date
    const scheduleNextMidnight = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 5, 0);
      const msUntilMidnight = midnight.getTime() - now.getTime();
      return setTimeout(() => {
        cachedDate = null;
        cacheTimestamp = 0;
        refreshDate();
        midnightTimer = scheduleNextMidnight();
      }, msUntilMidnight);
    };
    let midnightTimer = scheduleNextMidnight();

    return () => {
      clearInterval(interval);
      clearTimeout(midnightTimer);
    };
  }, []);

  // Ensure error is always a string or null, never an object
  // This prevents React Error #130 (rendering objects)
  const safeError = error && typeof error === 'string' ? error : null;
  
  return (
    <DateContext.Provider value={{ dateData, loading, error: safeError, refreshDate, setDateData: setDateDataAndCache }}>
      {children}
    </DateContext.Provider>
  );
}

export function useDate() {
  const context = useContext(DateContext);
  if (context === undefined) {
    // Return safe default instead of throwing to prevent error boundary from catching
    console.warn('⚠️ useDate called outside DateProvider - returning safe defaults');
    return {
      dateData: null,
      loading: false,
      error: null, // Always null, never a string or object
      refreshDate: async () => {},
      setDateData: (_: DateData | null) => {}
    };
  }
  // Ensure error is always a string or null, never an object
  // This prevents React Error #130 (rendering objects)
  const safeError = context.error && typeof context.error === 'string' ? context.error : null;
  return {
    ...context,
    error: safeError
  };
}

